import { ReactElement, RefCallback, useCallback, useEffect, useRef, useState } from 'react';
import { FormProps } from 'antd';
import {
  LoaderFunctionArgs,
  useNavigate,
} from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Form } from 'common/components/antd/Form/Form';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { BranchesCounterpartiesFilter } from
    'domains/counterparties/components/BranchesCounterpartiesFilter/BranchesCounterpartiesFilter';
import { useGetCounterpartiesQuery } from 'domains/counterparties/queries/counterparty.query';
import { TCounterpartiesListResData } from 'domains/counterparties/types/counterparty.types';
import {
  useAddPermissionToRoleMutation,
  useDeleteRoleMutation,
  useGetPermissionTableQuery,
  useRemovePermissionFromRoleMutation,
} from 'domains/roles/queries/roles.queries';
import {
  TPermission,
  TPermissionTableResData,
  TRow,
  TRolesListResData,
} from 'domains/roles/types/roles.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { ScrollerComponent } from './components/scroller/scroller.component';
import {
  ERoleSearchParamsKey,
  EUserManagementRolesPageGetParams,
  EUserManagementRolesPageGetParamsActionTypes,
} from './enums/roles.enum';
import CreateRoleModal from './modals/create-role.modal';
import {
  SContainer,
  SControlsContainer,
} from './roles.page.styles';
import PermissionTable, { TPermissionTableProps } from './table/permission.table';
import { KEY_SEPARATOR } from './table/table.constants';
import { ERoleMenuItemKey } from './table/table.enums';
import { TPayload, TScopeRecord, TTableColumn } from './table/table.types';
import { TCreateRoleModalData } from './types/roles.types';

type TCheckBoxStatus = {
  key: string,
  value: boolean,
}

const UserRoleManagementRolesPage = (): ReactElement => {
  const searchParams = new URLSearchParams(document.location.search);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const selectedCounterpartyGuid = searchParams.get(ERoleSearchParamsKey.CounterpartyGuid) || ENV_COUNTERPARTY_GUID;
  const scrollableTableNodeRef = useRef<Element>();
  const [modalData, setModalData] = useState<TCreateRoleModalData>({});
  const [field, setField] = useState<TCheckBoxStatus>({
    key: '',
    value: false,
  });

  const {
    data: permissionTableData,
    isLoading: isPermissionTableDataLoading,
    isError: isPermissionTableDataError,
    error: permissionTableDataError,
  } = useGetPermissionTableQuery(
    selectedCounterpartyGuid,
    {
      refetchOnMount: false,
    },
  );

  const {
    data: counterpartiesData,
    isLoading: isCounterpartiesDataLoading,
    isError: isCounterpartiesDataError,
    error: counterpartiesDataError,
  } = useGetCounterpartiesQuery(
    {},
    {
      refetchOnMount: false,
    },
  );

  const isLoading = isPermissionTableDataLoading || isCounterpartiesDataLoading;
  const isError = isPermissionTableDataError || isCounterpartiesDataError;
  const errorData = permissionTableDataError || counterpartiesDataError;

  const { mutateAsync: deleteRoleMutation } = useDeleteRoleMutation();
  const { mutateAsync: addPermissionToRoleMutation } = useAddPermissionToRoleMutation();
  const { mutateAsync: removePermissionFromRoleMutation } = useRemovePermissionFromRoleMutation();

  useEffect(() => {
    if (isError) {
      console.error(errorData);
    }
  }, [isError, errorData]);

  useEffect(() => {
    const pageAction = searchParams.get(EUserManagementRolesPageGetParams.Action);

    if (pageAction === EUserManagementRolesPageGetParamsActionTypes.Create) {
      setModalData({ counterpartiesData });
    }
  }, [document.location.search]);

  const sortPermissionTableData = (data: TPermissionTableResData | {}): TPermissionTableResData | {} => {
    const sorter = (a: TPermission | TRow, b: TPermission | TRow): number => a.sort_number - b.sort_number;
    ((data as TPermissionTableResData)?.rows || []).sort(sorter).forEach((row) => (row.permissions || []).sort(sorter));
    return data;
  };

  const sortedPermissionTableData = sortPermissionTableData(permissionTableData as TRolesListResData);

  const buildPermissionTableRows = (data: TPermissionTableResData | {}): TScopeRecord[] => {
    const { rows, roles } = data as TPermissionTableResData;

    const scopes: TScopeRecord[] = (rows || []).map((row) => {
      const generateUniqKey = row.permissions?.reduce((acc, item) => `${acc}#${item.guid}`, '');

      return {
        key: `${row.group_name}#${generateUniqKey}`,
        title: row.group_name,
        children: (row.permissions || []).map((permission) => ({
          key: permission.guid,
          scopeKey: row.group_name,
          title: permission.name,
          roles: (roles || []).reduce((acc, roleName) => {
            const rolePermission = (permission.roles || []).find((role_) => role_.name === roleName);
            return {
              [roleName]: !!rolePermission?.allowed,
              ...acc,
            };
          }, {}),
        })),
      };
    });

    return scopes;
  };

  const buildPermissionTableColumns = (data: TPermissionTableResData | {}): TTableColumn[] =>
    ((data as TPermissionTableResData).roles || []).map((roleName): { key: string, title: string } => ({
      key: roleName,
      title: roleName,
    }));

  const tableRows = buildPermissionTableRows(sortedPermissionTableData || {});
  const tableColumns = buildPermissionTableColumns(sortedPermissionTableData || {});

  const setTableContainerRef = useCallback<RefCallback<Element>>((node): void => {
    const tableContentNode = node?.querySelector('.ant-table-content');
    if (tableContentNode) {
      scrollableTableNodeRef.current = tableContentNode;
    }
  }, []);

  const formValuesChangeHandler: FormProps['onValuesChange'] = async (values): Promise<void> => {
    try {
      const payload: TPayload = Object.entries(values).map(([key, value]) => {
        if (typeof value === 'boolean') {
          setField({
            key,
            value,
          });
        }

        const state = values[key];
        const [columnKey, , permissionKey] = key.split(KEY_SEPARATOR);
        const column = tableColumns.find((c) => c.key === columnKey)!;

        return {
          roleName: column.title,
          permissionGuid: permissionKey,
          state,
        };
      });

      await Promise.all(payload.map(async (item): Promise<void> => {
        const { roleName, permissionGuid, state } = item;

        const reqParams = {
          role_name: roleName,
          permission_guid: permissionGuid,
        };
        if (state) {
          await addPermissionToRoleMutation({
            counterpartyGUID: selectedCounterpartyGuid,
            requestData: reqParams,
          });
        } else {
          await removePermissionFromRoleMutation({
            counterpartyGUID: selectedCounterpartyGuid,
            requestData: reqParams,
          });
        }
      }));

      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.RbacQueryKeys].getPermissionTableData._def,
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    form.setFieldValue(field?.key, field?.value);
  }, [formValuesChangeHandler]);

  const onColumnMenuItemClickHandler: TPermissionTableProps['onColumnMenuItemClick'] =
    async (values): Promise<void> => {
    try {
      const { key, column } = values;

      if (key === ERoleMenuItemKey.Delete) {
        const roleName = column.key;

        await deleteRoleMutation({
          counterpartyGUID: selectedCounterpartyGuid,
          requestData: {
            role_name: roleName,
          },
        });

        await queryClient.invalidateQueries({
          queryKey: queryKeysClient[EQueryKeys.RbacQueryKeys].getPermissionTableData._def,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onCancelModalHandler = (): void => {
    setModalData({});
    navigate(ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_ROLES);
  };

  return (
    <SContainer ref={setTableContainerRef}>
      <SControlsContainer>
        {IS_ADMIN_FE && (
          <BranchesCounterpartiesFilter
            counterpartiesData={counterpartiesData as TCounterpartiesListResData}
            selectedCounterpartyGuid={selectedCounterpartyGuid}
          />
        )}
        {scrollableTableNodeRef.current && <ScrollerComponent delta={200} node={scrollableTableNodeRef.current} />}
     </SControlsContainer>
      <PermissionTable
        onValuesChange={formValuesChangeHandler}
        form={form}
        widths={{
          role: 146,
          permission: 276,
        }}
        onColumnMenuItemClick={onColumnMenuItemClickHandler}
        loading={isLoading}
        rows={tableRows}
        columns={tableColumns}
      />
      <CreateRoleModal
        isOpen={!!Object.keys(modalData).length}
        onCancelModalHandler={onCancelModalHandler}
        data={modalData}
      />
    </SContainer>
  );
};

export const loader = async ({ request }: LoaderFunctionArgs): Promise<{
  counterpartiesData: TCounterpartiesListResData | {},
  permissionTableData: TPermissionTableResData | {},
}> => {
  try {
    const url = new URL(request.url);
    const searchParams = new URLSearchParams(url.search);

    const permissionTableQuery = queryKeysClient[EQueryKeys.RbacQueryKeys].getPermissionTableData(
      searchParams.get(ERoleSearchParamsKey.CounterpartyGuid) || ENV_COUNTERPARTY_GUID,
    );
    const permissionTableData: TPermissionTableResData =
      queryClient.getQueryData(permissionTableQuery.queryKey) ?? (await queryClient.fetchQuery(permissionTableQuery));

    const counterpartiesQuery = queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList();
    const counterpartiesData: TCounterpartiesListResData =
      queryClient.getQueryData(counterpartiesQuery.queryKey) ?? (await queryClient.fetchQuery(counterpartiesQuery));

    return {
      permissionTableData,
      counterpartiesData,
    };
  } catch (error) {
    console.error(error);

    return {
      permissionTableData: {},
      counterpartiesData: {},
    };
  }
};

export default UserRoleManagementRolesPage;
